import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface EditSelfRatingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  goalId: string;
  goalTitle: string;
  targetValue?: string;
  selfRating: string;
  onSaveSuccess: (goalId: string) => void;
}

const EditSelfRatingDialog = ({
  isOpen,
  onClose,
  goalId,
  goalTitle,
  targetValue,
  selfRating,
  onSaveSuccess
}: EditSelfRatingDialogProps) => {
  const [comments, setComments] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showEmptyInputDialog, setShowEmptyInputDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { activeYear } = usePerformanceYear();

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      // Delay to ensure the dialog is fully rendered and visible
      const timer = setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.select();
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const getSelfRatingLabel = (rating: string): string => {
    switch (rating) {
      case "far-below-expectations":
        return "Far Below Expectations";
      case "below-expectations":
        return "Below Expectations";
      case "meets-expectations":
        return "Meets Expectations";
      case "exceeds-expectations":
        return "Exceeds Expectations";
      case "far-exceeds-expectations":
        return "Far Exceeds Expectations";
      default:
        return "";
    }
  };

  const handleSave = () => {
    if (!comments.trim()) {
      setShowEmptyInputDialog(true);
      return;
    }
    setShowConfirmDialog(true);
  };

  const getSelfRatingValue = (rating: string): number | null => {
    switch (rating) {
      case "far-below-expectations":
        return 1;
      case "below-expectations":
        return 2;
      case "meets-expectations":
        return 3;
      case "exceeds-expectations":
        return 4;
      case "far-exceeds-expectations":
        return 5;
      default:
        return null;
    }
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const {
        data: {
          user
        },
        error: userError
      } = await supabase.auth.getUser();
      if (userError || !user) {
        setErrorMessage("Authentication required. Please log in and try again.");
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }

      // Get supervisor_id from users table
      const {
        data: userData,
        error: userDataError
      } = await supabase.from('users').select('supervisor_id').eq('id', user.id).single();
      if (userDataError) {
        console.error("Error fetching user data:", userDataError);
        setErrorMessage("Failed to fetch user information.");
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }
      const selfRatingValue = getSelfRatingValue(selfRating);
      if (selfRatingValue === null) {
        setErrorMessage("Please select a valid self rating before saving.");
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }

      // Check if a record already exists for this specific goal
      const {
        data: existingAppraisal,
        error: checkError
      } = await supabase.from('performance_appraisals').select('id').eq('user_id', user.id).eq('goal_id', goalId).eq('year', activeYear).maybeSingle();
      if (checkError) {
        console.error("Error checking existing appraisal:", checkError);
        setErrorMessage("Failed to check existing appraisal data.");
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }
      let saveError;
      if (existingAppraisal) {
        // Update existing record
        const {
          error
        } = await supabase.from('performance_appraisals').update({
          status: "draft",
          self_rating: selfRatingValue,
          self_comments: comments,
          employee_submission_date: new Date().toISOString(),
          year: activeYear
        }).eq('id', existingAppraisal.id);
        saveError = error;
      } else {
        // Create new record with goal_id
        const {
          error
        } = await supabase.from('performance_appraisals').insert({
          user_id: user.id,
          supervisor_id: userData.supervisor_id,
          goal_id: goalId,
          status: "draft",
          self_rating: selfRatingValue,
          manager_rating: null,
          self_comments: comments,
          manager_comments: null,
          employee_submission_date: new Date().toISOString(),
          manager_review_date: null,
          year: activeYear
        });
        saveError = error;
      }
      if (saveError) {
        console.error("Error saving self rating:", saveError);
        setErrorMessage(`Failed to save self rating: ${saveError.message}`);
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }

      // Success flow
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
      onSaveSuccess(goalId);
    } catch (error) {
      console.error("Error saving self rating:", error);
      setErrorMessage("An unexpected error occurred while saving.");
      setShowErrorDialog(true);
      setShowConfirmDialog(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    onClose();
    setComments("");
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
  };

  const handleEmptyInputDialogClose = () => {
    setShowEmptyInputDialog(false);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-white border-primary/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-black">Edit self rating comments for goal</DialogTitle>
            <DialogDescription className="text-gray-600">
              Goal: {goalTitle}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {targetValue && (
              <div>
                <label className="text-sm font-medium text-black mb-1 block">Target Value</label>
                <div className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded border">
                  {targetValue}
                </div>
              </div>
            )}
            
            {selfRating && (
              <div>
                <label className="text-sm font-medium text-black mb-1 block">Self Rating</label>
                <div className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded border">
                  {getSelfRatingLabel(selfRating)}
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-black mb-2 block">Self rating comments</label>
              <Textarea 
                ref={textareaRef}
                placeholder="Enter your self rating comments..." 
                value={comments} 
                onChange={e => setComments(e.target.value)} 
                className="w-full h-[150px] bg-white border-gray-300 text-black resize-none overflow-y-auto" 
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={onClose} 
              className="border-gray-400 text-black hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              className="bg-primary hover:bg-primary/90 text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showEmptyInputDialog} onOpenChange={setShowEmptyInputDialog}>
        <AlertDialogContent className="bg-white border-red-500/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Empty Input</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-700">
              Empty inputs cannot be saved. Please enter your comments before saving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={handleEmptyInputDialogClose} 
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-white border-primary/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Confirm Save</AlertDialogTitle>
            <AlertDialogDescription className="text-red-700">
              Are you sure you want to save your self rating comments for this goal? No changes can be made after saving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmDialog(false)} className="border-gray-400 text-black hover:bg-gray-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave} disabled={isSaving} className="bg-primary hover:bg-primary/90 text-white">
              {isSaving ? "Saving..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent className="bg-white border-red-500/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Error</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-700">
              {errorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleErrorDialogClose} className="bg-red-600 hover:bg-red-700 text-white">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="bg-white border-green-500/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-600">Success</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-700">
              Self rating for the goal has been successfully saved!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleSuccessDialogClose} className="bg-green-600 hover:bg-green-700 text-white">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditSelfRatingDialog;
